import React, { useCallback, useMemo, useRef, useState } from 'react';
import AllApoHoloSiteDto from '../models/AllApoHoloSiteDto';
import { ApoHoloSiteDto } from '../models/ApoHoloSiteDto';
import { PocketResiduesResult } from '../models/PocketResiduesResult';
import { SelectStructureDto } from '../models/SelectStructureDto';
import { StructureSequenceDto } from '../models/StructureSequenceDto';
import { AhojDataContext, AhojDataContextType } from './AhojDataContext';
import { AhojTableProvider } from './AhojTableContext';
import {
   ControlState,
   MolstarAction,
   MolstarRepresentationChangePayload,
   getDefaultChainColor,
   getDefaultPocketColor,
} from './ControlTypes';
import {
   MolstarHoverListener,
   MolstarListener,
   MolstarRepresentationChangeListener,
   StructureClickListener,
   StructureEventBusContext,
   StructureEventBusContextType,
} from './StructureEventBusContext';
import { StructureStateContext, StructureStateContextType } from './StructureStateContext';

/**
 * Root context provider for the AHoJ query-result viewer.
 *
 * It instantiates and wires up three independent context layers:
 *   1. `StructureEventBusContext` – pub/sub bus (listeners stored in `useRef` to avoid
 *      re-renders on every dispatch; stable identity guaranteed by `useCallback`).
 *   2. `AhojDataContext`          – backend data + RCSB selection + global colors.
 *   3. `StructureStateContext`    – per-row UI state (`controlsMap`, panel open state).
 *
 * `AhojTableProvider` is nested inside to derive the merged `AhojTableContext` that
 * class-based table components consume via `static contextType`.
 *
 * Each context value is wrapped in `useMemo` so that only the context(s) whose
 * dependencies actually changed cause downstream consumers to re-render.
 */
export const AhojProvider: React.FC<{
   children: React.ReactNode;
   apoHoloSites: AllApoHoloSiteDto;
   residueMapping: Map<string, PocketResiduesResult>;
   initialSelectedPdb?: SelectStructureDto;
   initialSelectedUniprot?: SelectStructureDto;
   colorPairCounts: Map<number, number>;
}> = ({ children, apoHoloSites, residueMapping, initialSelectedPdb, initialSelectedUniprot, colorPairCounts }) => {
   // Listeners are stored in refs (not state) so that dispatching never triggers a re-render.
   const listenersRef = useRef<MolstarListener[]>([]);
   const representationChangeListenersRef = useRef<MolstarRepresentationChangeListener[]>([]);
   const structureClickListenersRef = useRef<StructureClickListener[]>([]);
   const molstarHoverListenersRef = useRef<MolstarHoverListener[]>([]);
   const [controlsMap, setControlsMap] = useState<Map<string, ControlState>>(new Map());
   const [editingRowId, setEditingRowId] = useState<string | null>(null);
   const [infoRowId, setInfoRowId] = useState<string | null>(null);
   const [uniprotSequenceMap] = useState<Map<string, StructureSequenceDto>>(new Map());
   const [pdbSequenceMap] = useState<Map<string, StructureSequenceDto>>(new Map());
   const [selectedUniprot, setSelectedUniprot] = useState<SelectStructureDto | null>(initialSelectedUniprot || null);
   const [selectedPdb, setSelectedPdb] = useState<SelectStructureDto[]>(initialSelectedPdb ? [initialSelectedPdb] : []);

   const [globalPocketColor, setGlobalPocketColor] = useState<string>(getDefaultPocketColor('apo'));
   const [globalRestColor, setGlobalRestColor] = useState<string>(getDefaultChainColor('apo'));
   const [globalApoPocketColor, setGlobalApoPocketColor] = useState<string>(getDefaultPocketColor('apo'));
   const [globalAlpafoldPocketColor, setGlobalAlpafoldPocketColor] = useState<string>(
      getDefaultPocketColor('alphafold'),
   );
   const [globalHoloPocketColor, setGlobalHoloPocketColor] = useState<string>(getDefaultPocketColor('holo'));
   const [globalApoRestColor, setGlobalApoRestColor] = useState<string>(getDefaultChainColor('apo'));
   const [globalHoloRestColor, setGlobalHoloRestColor] = useState<string>(getDefaultChainColor('holo'));
   const [globalAlpafoldRestColor, setGlobalAlpafoldRestColor] = useState<string>(getDefaultChainColor('alphafold'));

   /**
    * Registers a `MolstarAction` listener and returns a cleanup function.
    * `useCallback` with an empty dep array ensures the function reference is stable
    * for the lifetime of the provider, preventing unnecessary re-subscriptions.
    */
   const subscribe = useCallback((listener: MolstarListener) => {
      listenersRef.current.push(listener);
      return () => {
         listenersRef.current = listenersRef.current.filter((l) => l !== listener);
      };
   }, []);

   /** Registers a representation/color change listener and returns a cleanup function. */
   const subscribeRepresentationChange = useCallback((listener: MolstarRepresentationChangeListener) => {
      representationChangeListenersRef.current.push(listener);
      return () => {
         representationChangeListenersRef.current = representationChangeListenersRef.current.filter(
            (l) => l !== listener,
         );
      };
   }, []);

   /** Registers a structure-click listener and returns a cleanup function. */
   const subscribeStructureClick = useCallback((listener: StructureClickListener) => {
      structureClickListenersRef.current.push(listener);
      return () => {
         structureClickListenersRef.current = structureClickListenersRef.current.filter((l) => l !== listener);
      };
   }, []);

   /** Synchronously calls all registered `MolstarAction` listeners. */
   const dispatch = useCallback((action: MolstarAction) => {
      listenersRef.current.forEach((listener) => listener(action));
   }, []);

   /** Synchronously calls all registered representation-change listeners. */
   const dispatchRepresentationChange = useCallback((payload: MolstarRepresentationChangePayload) => {
      representationChangeListenersRef.current.forEach((listener) => listener(payload));
   }, []);

   /** Synchronously calls all registered structure-click listeners. */
   const dispatchStructureClick = useCallback((rowDto: ApoHoloSiteDto) => {
      structureClickListenersRef.current.forEach((listener) => listener(rowDto));
   }, []);

   /** Synchronously notifies all Molstar-hover listeners of the hovered residue seq id. */
   const dispatchMolstarHover = useCallback((seqId: number | null) => {
      molstarHoverListenersRef.current.forEach((listener) => listener(seqId));
   }, []);

   /** Registers a Molstar residue-hover listener and returns a cleanup function. */
   const subscribeMolstarHover = useCallback((listener: MolstarHoverListener) => {
      molstarHoverListenersRef.current.push(listener);
      return () => {
         molstarHoverListenersRef.current = molstarHoverListenersRef.current.filter((l) => l !== listener);
      };
   }, []);

   /**
    * Immutably updates a single `ControlState` entry in `controlsMap` by creating a
    * new Map on every call. React's referential equality check on the Map means a
    * new Map is required to trigger a re-render; mutating in place would be invisible.
    */
   const setControl = useCallback((rowId: string, control: ControlState) => {
      setControlsMap((prev) => {
         const next = new Map(prev);
         next.set(rowId, control);
         return next;
      });
   }, []);

   const busValue = useMemo<StructureEventBusContextType>(
      () => ({
         dispatch,
         dispatchRepresentationChange,
         dispatchStructureClick,
         subscribe,
         subscribeRepresentationChange,
         subscribeStructureClick,
         dispatchMolstarHover,
         subscribeMolstarHover,
      }),
      [
         dispatch,
         dispatchRepresentationChange,
         dispatchStructureClick,
         subscribe,
         subscribeRepresentationChange,
         subscribeStructureClick,
         dispatchMolstarHover,
         subscribeMolstarHover,
      ],
   );

   const stateValue = useMemo<StructureStateContextType>(
      () => ({
         controlsMap,
         setControl,
         editingRowId,
         setEditingRowId,
         infoRowId,
         setInfoRowId,
         colorPairCounts,
      }),
      [controlsMap, setControl, editingRowId, setEditingRowId, infoRowId, setInfoRowId, colorPairCounts],
   );

   const dataValue = useMemo<AhojDataContextType>(
      () => ({
         apoHoloSites,
         residueMapping,
         uniprotSequenceMap,
         pdbSequenceMap,
         selectedUniprot,
         selectedPdb,
         setSelectedPdb,
         setSelectedUniprot,
         globalApoPocketColor,
         globalHoloPocketColor,
         globalPocketColor,
         globalAlpafoldPocketColor,
         globalApoRestColor,
         globalHoloRestColor,
         globalRestColor,
         globalAlpafoldRestColor,
         setGlobalApoPocketColor,
         setGlobalHoloPocketColor,
         setGlobalPocketColor,
         setGlobalAlpafoldPocketColor,
         setGlobalApoRestColor,
         setGlobalHoloRestColor,
         setGlobalRestColor,
         setGlobalAlpafoldRestColor,
      }),
      [
         apoHoloSites,
         residueMapping,
         uniprotSequenceMap,
         pdbSequenceMap,
         selectedUniprot,
         selectedPdb,
         setSelectedPdb,
         setSelectedUniprot,
         globalApoPocketColor,
         globalHoloPocketColor,
         globalPocketColor,
         globalAlpafoldPocketColor,
         globalApoRestColor,
         globalHoloRestColor,
         globalRestColor,
         globalAlpafoldRestColor,
         setGlobalApoPocketColor,
         setGlobalHoloPocketColor,
         setGlobalPocketColor,
         setGlobalAlpafoldPocketColor,
         setGlobalApoRestColor,
         setGlobalHoloRestColor,
         setGlobalRestColor,
         setGlobalAlpafoldRestColor,
      ],
   );

   return (
      <StructureEventBusContext.Provider value={busValue}>
         <AhojDataContext.Provider value={dataValue}>
            <StructureStateContext.Provider value={stateValue}>
               <AhojTableProvider>{children}</AhojTableProvider>
            </StructureStateContext.Provider>
         </AhojDataContext.Provider>
      </StructureEventBusContext.Provider>
   );
};
